import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import type { Prayer } from '@flock-types/prayer.types';
import { useAuth } from '@/context/AuthContext';
import { useCombinedPrayers } from '@/hooks/useCombinedPrayers';
import { usePrayerActions } from '@/hooks/usePrayerActions';
import PrayerCard from './PrayerCard';

const STATUS_TABS = [
  { value: 'active', label: 'Active' },
  { value: 'answered', label: 'Answered' },
  { value: 'all', label: 'All' },
] as const;

const TYPE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'prayer', label: 'Prayers' },
  { value: 'praise', label: 'Praises' },
] as const;

interface Props {
  mode: 'all' | 'lifted';
}

/**
 * Combined cross-group prayer feed. "All Prayers" merges every group across
 * every org (lifted first, then newest); "Lifted" narrows to prayers
 * currently in their 48-hour lifted window. Cards carry a group chip so you
 * always know where a prayer came from.
 */
export default function CombinedPrayersPage({ mode }: Props) {
  const { user, organizations } = useAuth();
  const [status, setStatus] = useState<'active' | 'answered' | 'all'>('active');
  const [typeFilter, setTypeFilter] = useState<'all' | 'prayer' | 'praise'>('all');
  const [searchText, setSearchText] = useState('');

  // Lifted view is inherently "active" — status tabs apply to All Prayers only
  const effectiveStatus = mode === 'lifted' ? 'active' : status;
  const { prayers, loading, partialFailure, errors, groupsWithMore, refetch } =
    useCombinedPrayers(mode, effectiveStatus);
  const actions = usePrayerActions();

  const showOrg = organizations.length > 1;

  // Per-org leader map for the menu (delete) on cross-org cards
  const leaderOrgIds = useMemo(() => {
    const LEADER_ROLES = ['org_admin', 'campus_pastor', 'ministry_director', 'group_leader'];
    return new Set(
      organizations
        .filter((o) => (o.roles ?? []).some((r) => LEADER_ROLES.includes(r.role)))
        .map((o) => o.organization_id)
    );
  }, [organizations]);

  const visiblePrayers = useMemo(() => {
    let list = prayers;
    if (typeFilter !== 'all') list = list.filter((p) => p.type === typeFilter);
    const q = searchText.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.prayerText.toLowerCase().includes(q) ||
          p.userName.toLowerCase().includes(q) ||
          p.groupName.toLowerCase().includes(q)
      );
    }
    return list;
  }, [prayers, typeFilter, searchText]);

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-flock-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-flock-navy-900">
              {mode === 'lifted' ? 'Lifted Prayers' : 'All Prayers'}
            </h1>
            <p className="text-xs text-flock-navy-400">
              {mode === 'lifted'
                ? 'Prayers lifted back up across all your groups'
                : 'Every prayer across all your groups'}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {mode === 'all' && (
            <div className="flex gap-1 rounded-full bg-flock-navy-50 p-1">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setStatus(tab.value)}
                  className={clsx(
                    'rounded-full px-3 py-1 text-xs font-medium transition',
                    status === tab.value
                      ? 'bg-flock-navy-600 text-white'
                      : 'text-flock-navy-500 hover:text-flock-navy-700'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-1 rounded-full bg-flock-navy-50 p-1">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setTypeFilter(tab.value)}
                className={clsx(
                  'rounded-full px-3 py-1 text-xs font-medium transition',
                  typeFilter === tab.value
                    ? 'bg-flock-navy-600 text-white'
                    : 'text-flock-navy-500 hover:text-flock-navy-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search prayers"
            className="ml-auto w-56 rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-1.5 text-sm outline-none focus:border-flock-navy-400"
          />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-3">
          {partialFailure && (
            <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <span>
                Couldn't load prayers from{' '}
                {errors.map((e) => e.groupName).join(', ')}.
              </span>
              <button onClick={refetch} className="font-semibold hover:underline">
                Retry
              </button>
            </div>
          )}

          {loading && prayers.length === 0 && (
            <div className="py-12 text-center text-sm text-flock-navy-400">Loading prayers…</div>
          )}

          {!loading && visiblePrayers.length === 0 && (
            <div className="py-12 text-center">
              {mode === 'lifted' ? (
                <>
                  <p className="text-2xl">🕯️</p>
                  <p className="mt-2 text-sm font-medium text-flock-navy-600">
                    No prayers are currently lifted
                  </p>
                  <p className="mt-1 text-xs text-flock-navy-400">
                    When someone lifts a prayer, it will glow here for 48 hours.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-flock-navy-600">
                    No prayers across your groups yet
                  </p>
                  <p className="mt-1 text-xs text-flock-navy-400">
                    Open one of your groups to share the first prayer.
                  </p>
                </>
              )}
            </div>
          )}

          {visiblePrayers.map((prayer) => (
            <PrayerCard
              key={prayer.id}
              prayer={prayer}
              currentUserId={user?.id}
              isLeader={leaderOrgIds.has(prayer.organizationId)}
              context={{
                groupName: prayer.groupName,
                organizationName: showOrg ? prayer.organizationName : undefined,
              }}
              onPray={(p) => actions.pray(p).catch(() => {})}
              onEncourage={(p, note) => actions.encourage(p, note)}
              onReactionToggle={(p, noteId, emoji, reacted) =>
                actions.toggleEncouragementReaction(p, noteId, emoji, reacted).catch(() => {})
              }
              onMarkAnswered={(p: Prayer, answerText: string) =>
                actions.markAnswered(p, answerText || undefined)
              }
              onLiftToggle={(p) => actions.liftToggle(p).catch(() => {})}
              onEdit={(p, newText) => actions.edit(p, newText)}
              onDelete={(p) => {
                if (window.confirm('Delete this prayer?')) actions.remove(p).catch(() => {});
              }}
            />
          ))}

          {groupsWithMore.length > 0 && visiblePrayers.length > 0 && (
            <div className="py-2 text-center text-xs text-flock-navy-400">
              Showing the most recent prayers per group — see more in{' '}
              {groupsWithMore.map((g, i) => (
                <span key={g.groupId}>
                  {i > 0 && ', '}
                  <Link
                    to={`/prayers/${g.groupId}`}
                    className="font-medium text-flock-navy-600 hover:underline"
                  >
                    {g.groupName}
                  </Link>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
